import { EventsService } from "@/api/events";
import EventCard, { EventCardProps } from "./EventCard";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect, useState, forwardRef } from "react";
import EventSkeletonLoader from "./SkeletonLoaders/EventSkeletonLoader";

// Sample events data
const sampleEvents: EventCardProps[] = [
  {
    id: "1",
    title: "Tech Innovation Summit 2023",
    description:
      "Join industry leaders for discussions on emerging technologies and digital transformation strategies.",
    date: "Dec 15, 2023",
    time: "9:00 AM - 5:00 PM",
    location: "San Francisco, CA",
    image:
      "https://images.unsplash.com/photo-1540575467063-178a50c2df87?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80",
    attendees: 380,
    maxAttendees: 400,
    price: "$299",
    category: "Technology",
    featured: true,
  },
  {
    id: "2",
    title: "Digital Marketing Masterclass",
    description:
      "Learn the latest digital marketing strategies to grow your business online.",
    date: "Nov 25, 2023",
    time: "10:00 AM - 3:00 PM",
    location: "Virtual Event",
    image:
      "https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80",
    attendees: 210,
    maxAttendees: 300,
    price: "$149",
    category: "Marketing",
  },
  {
    id: "3",
    title: "Healthcare Innovation Conference",
    description:
      "Exploring the future of healthcare technology and patient care models.",
    date: "Jan 10, 2024",
    time: "8:30 AM - 4:30 PM",
    location: "Boston, MA",
    image:
      "https://images.unsplash.com/photo-1576091160550-2173dba999ef?ixlib=rb-4.0.3&auto=format&fit=crop&w=1170&q=80",
    attendees: 150,
    maxAttendees: 200,
    price: "$249",
    category: "Healthcare",
  },
];

const FeaturedEvents = forwardRef<HTMLDivElement>((_, ref) => {
  const { exploreEvent } = EventsService;
  const [events, setEvents] = useState<EventCardProps[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchFeaturedEvents = async () => {
    setLoading(true);
    try {
      const response: any = await exploreEvent();
      if (response.statusCode === 200) {
        const formattedEvents = response.body.events.map((event) => ({
          id: event.event_id,
          title: event.event_title,
          description: event.event_description,
          date: new Date(event.event_date).toLocaleDateString(),
          time: `${event.start_time} - ${event.end_time}`,
          location: event.location,
          meetingLink: event.meeting_link,
          image: event.img_url?.[0],
          attendees: 0, // default until backend supports it
          // maxAttendees: event.max_attendees,
          price: `$${event?.ticket_price ?? "0"}`,
          category: event.category,
          isDigital: event.virtual_event,
        }));

        setEvents(formattedEvents);
      }
    } catch (error) {
      console.error("Error fetching featured events:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeaturedEvents();
  }, []);

  return (
    <section className="py-16 bg-white" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row justify-between items-center mb-10">
          <div>
            <h2 className="text-3xl font-bold text-cecure-900">
              Featured Events
            </h2>
            <p className="mt-2 text-lg text-gray-600">
              Discover upcoming events you won't want to miss
            </p>
          </div>
          <Link to="/all-events">
            <Button variant="outline" className="mt-4 md:mt-0">
              View All Events
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
        {loading ? (
          <EventSkeletonLoader />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {events.slice(0, 3).map((event) => (
              <EventCard key={event.id} {...(event as EventCardProps)} />
            ))}
          </div>
        )}

        <div className="mt-12 text-center">
          <Link to="/create">
            <Button size="lg" className="bg-cecure-700 hover:bg-cecure-800">
              Create Your Own Event
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
});

export default FeaturedEvents;
